import React, { useState, useEffect } from "react";
import {
  getPreviousOwners,
  getTenantById,
} from "../../../../../shared/services/apis";
import IframeViewer from "../../../../../shared/ui/urlOpener/IFrameViewer";
import jsPDF from "jspdf";
import saveAs from "file-saver";
import { RiDownloadLine } from "react-icons/ri";
import { Footer, ImageRun } from "docx";

interface PreviousOwnersProps {
  tenantId: string;
  tenantServiceId: string;
  tenantServiceOptionId: string;
}

export interface Source {
  Title: string;
  URL: string;
}

export interface OtherShareholder {
  Name: string;
  Company_Name: string;
  Company_Website: string;
  Ownership_Percent: string;
  Type: string;
  Sources: Source[];
}

export interface Transaction {
  Date: string;
  Event: string;
  Details: string;
  Sell_Side_Advisors: string[];
  Buy_Side_Advisors: string[];
  Sources: Source[];
}

export interface OwnershipContent {
  Ownership: string;
  Primary_Owner: string;
  Ownership_Percent: string;
  Ownership_Type: string;
  Sale_Value: string;
  Other_Shareholders: OtherShareholder[];
  Data_Available: string;
  Transactions: Transaction[];
}

export interface OwnershipData {
  citations: string[];
  search_results: {
    title: string;
    url: string;
    date: string;
    last_updated: string;
  }[];
  content: OwnershipContent;
}
interface Tenant {
  id: string;
  name: string;
  logoUrl?: string;
}

// Loading Skeleton Component
const LoadingSkeleton = () => {
  return (
    <div className="bg-[#FDFDFD] rounded-xl mt-0 2xl:mt-20 w-full max-w-4xl mx-auto overflow-y-auto max-h-[70vh] shadow-xs border border-gray-200 mb-4 sm:mb-8 md:mb-30 animate-pulse">
      {/* Header Section Skeleton */}
      <div className="p-3 sm:p-4 md:p-6 bg-[#FAFBFD] rounded-t-2xl flex-shrink-0 border-b border-[#E5E7EB]">
        {/* Title Skeleton */}
        <div className="h-6 sm:h-7 md:h-8 bg-gray-200 rounded-md mb-3 md:mb-4 w-3/4"></div>

        <div className="space-y-2 sm:space-y-3">
          {/* Primary Owner Skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start sm:items-center">
            <div className="sm:col-span-4">
              <div className="h-4 sm:h-5 bg-gray-200 rounded w-full"></div>
            </div>
            <div className="col-span-2">
              <div className="h-4 sm:h-5 bg-gray-200 rounded w-16"></div>
            </div>
            <div className="sm:col-span-3">
              <div className="h-4 sm:h-5 bg-gray-200 rounded w-24"></div>
            </div>
            <div className="sm:col-span-3">
              <div className="h-4 sm:h-5 bg-gray-200 rounded w-20"></div>
            </div>
          </div>

          {/* Other Shareholders Skeleton (2 rows) */}
          {[1, 2].map((item) => (
            <div
              key={item}
              className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start sm:items-center"
            >
              <div className="sm:col-span-4">
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div className="sm:col-span-2">
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-12"></div>
              </div>
              <div className="sm:col-span-3">
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline Section Skeleton */}
      <div className="p-3 sm:p-4 md:p-6 relative flex-1 overflow-y-auto">
        {/* Dashed line skeleton */}
        <div className="hidden sm:block absolute left-32 md:left-50 top-0 bottom-0 w-0.5 border-l border-dashed border-gray-300"></div>

        <div className="space-y-6 sm:space-y-8 md:space-y-12">
          {/* Transaction Skeletons (2 items) */}
          {[1, 2].map((item) => (
            <div
              key={item}
              className="relative flex flex-col sm:flex-row sm:items-start"
            >
              {/* Date and Event Skeleton */}
              <div className="w-full sm:w-32 md:w-44 sm:text-right sm:pr-4 flex-shrink-0 mb-4 sm:mb-0">
                <div className="h-4 bg-gray-200 rounded mb-2 w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 mt-1"></div>
              </div>

              {/* Timeline dot skeleton */}
              <div className="hidden sm:block w-3 h-3 rounded-full bg-gray-200 shadow absolute left-[7.5rem] md:left-[10.625rem] top-[2rem] z-10"></div>

              {/* Content Skeleton */}
              <div className="flex-1 sm:ml-4 md:ml-6 sm:mt-[1.75rem]">
                {/* Details skeleton */}
                <div className="space-y-2 mb-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>

                {/* Advisors skeleton */}
                <div className="space-y-1 mb-2">
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>

                {/* Sources skeleton */}
                <div className="flex gap-1 flex-wrap">
                  {[1, 2, 3].map((source) => (
                    <div
                      key={source}
                      className="h-6 bg-gray-200 rounded-xl w-16"
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const PreviousOwners: React.FC<PreviousOwnersProps> = ({
  tenantId,
  tenantServiceId,
  tenantServiceOptionId,
}) => {
  const [data, setData] = useState<OwnershipContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [tenant, setTenant] = useState<Tenant | null>();

  useEffect(() => {
    if (!tenantId) return;

    const fetchTenant = async () => {
      try {
        const data = await getTenantById(tenantId);
        setTenant(data);
      } catch (error) {
        console.error("Error fetching tenant:", error);
      }
    };

    fetchTenant();
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !tenantServiceId || !tenantServiceOptionId) return;

    const fetchTenantData = async () => {
      try {
        const response: OwnershipData = await getPreviousOwners(
          tenantId,
          tenantServiceId,
          tenantServiceOptionId
        );
        setData(response?.content || null);
      } catch (error) {
        console.error("Error fetching tenant overview:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [tenantId, tenantServiceId, tenantServiceOptionId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return dateString;

    try {
      const date = new Date(dateString);
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const year = date.getFullYear();

      return `${day} ${month}, ${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const getHostname = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace("www.", "");
    } catch (error) {
      const match = url.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/);
      return match ? match[1] : url;
    }
  };

  const handleSourceClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    setIframeUrl(url);
  };

  const downloadPDF = async () => {
    if (!data) return;
    setDownloadingPdf(true);
    try {
      // Load the watermark image with proper typing
      const loadImage = (url: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "Anonymous";
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = url;
        });
      };

      // Load watermark image if logoUrl exists
      let watermarkImg: HTMLImageElement | null = null;
      if (tenant?.logoUrl) {
        try {
          watermarkImg = await loadImage(tenant.logoUrl);
        } catch (error) {
          console.warn("Failed to load watermark image:", error);
        }
      }

      // Function to add watermark to a page
      const addWatermark = (pageNumber: number) => {
        if (!watermarkImg) return;

        doc.setPage(pageNumber);

        // Save graphics state and set transparency
        doc.saveGraphicsState();
        doc.setGState(doc.GState({ opacity: 0.1 })); // Remove 'new' keyword

        // Calculate watermark size and position (centered)
        const watermarkWidth = 80;
        const watermarkHeight =
          (watermarkImg.height / watermarkImg.width) * watermarkWidth;
        const xPos = (pageWidth - watermarkWidth) / 2;
        const yPos = (pageHeight - watermarkHeight) / 2;

        // Add watermark image
        doc.addImage(
          watermarkImg,
          "PNG",
          xPos,
          yPos,
          watermarkWidth,
          watermarkHeight
        );

        // Restore graphics state
        doc.restoreGraphicsState();
      };
      const doc = new jsPDF();
      const margin = 15;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let y = margin;

      // Title - big and bold
      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.setTextColor("#2c3e50");
      doc.text("Ownership Report", pageWidth / 2, y, { align: "center" });
      y += 18;

      // Draw line below title
      doc.setDrawColor("#bdc3c7");
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageWidth - margin, y);
      y += 12;

      // Metadata section with labels in bold
      const metaEntries = [
        { label: "Ownership", value: data.Ownership },
        { label: "Primary Owner", value: data.Primary_Owner },
        { label: "Ownership Percent", value: data.Ownership_Percent },
        { label: "Ownership Type", value: data.Ownership_Type },
        { label: "Sale Value", value: data.Sale_Value },
      ];

      doc.setFontSize(12);
      for (const { label, value } of metaEntries) {
        if (y > pageHeight - margin - 15) {
          doc.addPage();
          y = margin;
        }
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#34495e");
        doc.text(`${label}:`, margin, y);
        const labelWidth = doc.getTextWidth(`${label}: `) + 2;
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#2c3e50");
        doc.text(value || "N/A", margin + labelWidth, y);
        y += 10;
      }
      y += 10;

      // Other Shareholders with section header
      if (data.Other_Shareholders && data.Other_Shareholders.length) {
        if (y > pageHeight - margin - 15) {
          doc.addPage();
          y = margin;
        }
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#2980b9");
        doc.text("Other Shareholders", margin, y);
        y += 14;

        // Table header
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#34495e");
        const headers = ["Name", "Company", "Ownership %", "Type"];
        const colWidths = [50, 70, 35, 35];
        let xPos = margin;
        for (let i = 0; i < headers.length; i++) {
          doc.text(headers[i], xPos, y);
          xPos += colWidths[i];
        }
        y += 8;

        // Draw header line
        doc.setDrawColor("#95a5a6");
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageWidth - margin, y);
        y += 6;

        doc.setFont("helvetica", "normal");
        doc.setTextColor("#2c3e50");

        // Rows
        for (const shareholder of data.Other_Shareholders) {
          if (y > pageHeight - margin - 20) {
            doc.addPage();
            y = margin;
          }
          xPos = margin;
          const rowData = [
            shareholder.Name || "",
            shareholder.Company_Name || "",
            shareholder.Ownership_Percent || "",
            shareholder.Type || "",
          ];
          for (let i = 0; i < rowData.length; i++) {
            doc.text(rowData[i], xPos, y);
            xPos += colWidths[i];
          }
          y += 9;
        }
        y += 10;
      }

      // Transactions section header
      if (data.Transactions && data.Transactions.length) {
        if (y > pageHeight - margin - 15) {
          doc.addPage();
          y = margin;
        }
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor("#2980b9");
        doc.text("Transactions", margin, y);
        y += 14;

        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor("#2c3e50");

        for (const tx of data.Transactions) {
          if (y > pageHeight - margin - 60) {
            doc.addPage();
            y = margin;
          }
          doc.setFont("helvetica", "bold");
          doc.text(`Event: `, margin, y);
          doc.setFont("helvetica", "normal");
          doc.text(tx.Event, margin + 20, y);
          y += 8;

          doc.setFont("helvetica", "bold");
          doc.text(`Date: `, margin, y);
          doc.setFont("helvetica", "normal");
          doc.text(tx.Date, margin + 20, y);
          y += 8;

          const detailLines = doc.splitTextToSize(
            tx.Details,
            pageWidth - margin * 2
          );
          for (const lineText of detailLines) {
            if (y > pageHeight - margin) {
              doc.addPage();
              y = margin;
            }
            doc.text(lineText, margin, y);
            y += 8;
          }

          if (tx.Sell_Side_Advisors && tx.Sell_Side_Advisors.length) {
            doc.text(
              `Sell Side Advisors: ${tx.Sell_Side_Advisors.join(", ")}`,
              margin,
              y
            );
            y += 8;
          }
          if (tx.Buy_Side_Advisors && tx.Buy_Side_Advisors.length) {
            doc.text(
              `Buy Side Advisors: ${tx.Buy_Side_Advisors.join(", ")}`,
              margin,
              y
            );
            y += 8;
          }
          y += 12;
        }
      }
      // Add watermark to all pages
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        addWatermark(i);
      }

      // Footer on every page
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
          align: "center",
        });
      }

      doc.save(`Ownership_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (error) {
      alert(
        `Failed to download PDF: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadWord = async () => {
    if (!data) return;
    setDownloadingWord(true);

    try {
      const {
        Document,
        Paragraph,
        Packer,
        HeadingLevel,
        AlignmentType,
        TextRun,
      } = await import("docx");
      const base64ToUint8Array = (base64: string): Uint8Array => {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      // Load logo image if logoUrl exists
      let logoImageData: {
        data: Uint8Array;
        mimeType: "jpg" | "png" | "gif" | "bmp";
      } | null = null;
      if (tenant?.logoUrl) {
        try {
          console.log("Loading logo from:", tenant.logoUrl);
          const response = await fetch(tenant.logoUrl);
          const blob = await response.blob();

          // Map MIME type to docx accepted format
          const getMimeType = (
            blobType: string
          ): "jpg" | "png" | "gif" | "bmp" => {
            if (blobType.includes("png")) return "png";
            if (blobType.includes("jpeg") || blobType.includes("jpg"))
              return "jpg";
            if (blobType.includes("gif")) return "gif";
            if (blobType.includes("bmp")) return "bmp";
            return "png";
          };

          const mimeType = getMimeType(blob.type);
          console.log("Detected MIME type:", mimeType);

          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(",")[1];
              resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

          const uint8Array = base64ToUint8Array(base64);
          console.log("Logo loaded, size:", uint8Array.length, "bytes");

          logoImageData = { data: uint8Array, mimeType };
        } catch (error) {
          console.error("Failed to load logo image:", error);
        }
      }

      const footerChildren = [
        new Paragraph({
          children: [
            new TextRun({
              text: `Confidential - Generated by ${tenant?.name || "Company"}`,
              size: 10,
              color: "888888",
            }),
          ],
        }),
      ];

      if (logoImageData) {
        footerChildren.push(
          new Paragraph({
            children: [
              new ImageRun({
                type: logoImageData.mimeType,
                data: logoImageData.data,
                transformation: {
                  width: 60,
                  height: 60,
                },
              }),
            ],
            alignment: AlignmentType.RIGHT,
            spacing: { before: 100 },
          })
        );
      }
      const doc = new Document({
        sections: [
          { footers: {
              default: new Footer({ children: footerChildren }),
            },
            children: [
              new Paragraph({
                text: "Ownership Report",
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
              }),
              new Paragraph({
                text: `Generated on ${new Date().toLocaleDateString()}`,
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
              }),
              new Paragraph({
                text: "Ownership Information",
                heading: HeadingLevel.HEADING_2,
              }),
              new Paragraph({
                text: `Ownership: ${data.Ownership}`,
                spacing: { after: 100 },
              }),
              new Paragraph({
                text: `Primary Owner: ${data.Primary_Owner}`,
                spacing: { after: 100 },
              }),
              new Paragraph({
                text: `Ownership Percent: ${data.Ownership_Percent}`,
                spacing: { after: 100 },
              }),
              new Paragraph({
                text: `Ownership Type: ${data.Ownership_Type}`,
                spacing: { after: 200 },
              }),

              ...(data.Other_Shareholders && data.Other_Shareholders.length > 0
                ? [
                    new Paragraph({
                      text: "Other Shareholders",
                      heading: HeadingLevel.HEADING_2,
                      spacing: { after: 100 },
                    }),
                    ...data.Other_Shareholders.map(
                      (shareholder) =>
                        new Paragraph({
                          text: `${shareholder.Name} | ${shareholder.Company_Name} | ${shareholder.Ownership_Percent} | ${shareholder.Type}`,
                          spacing: { after: 60 },
                        })
                    ),
                  ]
                : []),

              ...(data.Transactions && data.Transactions.length > 0
                ? [
                    new Paragraph({
                      text: "Transactions",
                      heading: HeadingLevel.HEADING_2,
                      spacing: { after: 100 },
                    }),
                    ...data.Transactions.flatMap((tx) => [
                      new Paragraph({
                        text: `${tx.Event} - ${formatDate(tx.Date)}`,
                        spacing: { after: 60 },
                      }),
                      new Paragraph({
                        text: tx.Details,
                        spacing: { after: 60 },
                      }),
                      ...(tx.Sell_Side_Advisors &&
                      tx.Sell_Side_Advisors.length > 0
                        ? [
                            new Paragraph({
                              text: `Sell Side Advisors: ${tx.Sell_Side_Advisors.join(
                                ", "
                              )}`,
                              spacing: { after: 60 },
                            }),
                          ]
                        : []),
                      ...(tx.Buy_Side_Advisors &&
                      tx.Buy_Side_Advisors.length > 0
                        ? [
                            new Paragraph({
                              text: `Buy Side Advisors: ${tx.Buy_Side_Advisors.join(
                                ", "
                              )}`,
                              spacing: { after: 60 },
                            }),
                          ]
                        : []),
                    ]),
                  ]
                : []),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(
        blob,
        `Ownership_Report_${new Date().toISOString().slice(0, 10)}.docx`
      );
    } catch (error) {
      alert(
        `Failed to download Word document: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setDownloadingWord(false);
    }
  };

  // Show loading skeleton
  if (loading) return <LoadingSkeleton />;

  if (!data)
    return (
      <div className="flex justify-center items-center py-12 px-4">
        <p className="text-sm sm:text-base">No previous owner data available</p>
      </div>
    );

  return (
    <>
      <div className="bg-[#FDFDFD] rounded-xl mt-0 2xl:mt-20 w-full max-w-4xl mx-auto  shadow-xs border border-gray-200 ">
        {/* Header Section */}
        <div className="flex gap-2 justify-end mt-2">
          <button
            onClick={downloadPDF}
            disabled={downloadingPdf}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingPdf ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" /> PDF
              </>
            )}
          </button>

          <button
            onClick={downloadWord}
            disabled={downloadingWord}
            className="flex items-center gap-2 hover:bg-gray-200 font-light px-4 py-2 rounded-full border border-gray-300 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {downloadingWord ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </>
            ) : (
              <>
                <RiDownloadLine className="w-4 h-4" />
                Word
              </>
            )}
          </button>
        </div>
        <div className="p-3 sm:p-4 md:p-6 bg-[#FAFBFD] rounded-t-2xl flex-shrink-0 border-b border-[#E5E7EB]">
          <h2 className="text-lg sm:text-xl md:text-2xl font-light text-gray-900 mb-3 md:mb-4 break-words">
            Ownership: {data.Ownership}
          </h2>

          <div className="space-y-2 sm:space-y-3">
            {/* Primary Owner */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start sm:items-center">
              <div className="sm:col-span-4">
                <span className="text-sm sm:text-base font-normal text-gray-900 block break-words">
                  {data.Primary_Owner}
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-base text-gray-900">
                  {data.Ownership_Percent && (
                    <span className="text-base text-gray-900">
                      {data.Ownership_Percent.includes("%")
                        ? data.Ownership_Percent
                        : `${data.Ownership_Percent}%`}
                    </span>
                  )}
                </span>
              </div>
              <div className="sm:col-span-3">
                <span className="text-sm sm:text-base font-light text-gray-900 block">
                  {data.Ownership_Type}
                </span>
              </div>
              <div className="sm:col-span-3">
                <span className="text-sm sm:text-base text-gray-900 block break-words">
                  Sold for {data.Sale_Value}
                </span>
              </div>
            </div>

            {/* Other Shareholders */}
            {data.Other_Shareholders?.map((owner, index) => (
              <div
                key={index}
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start sm:items-center"
              >
                <div className="sm:col-span-4">
                  <span
                    className={`text-sm sm:text-base font-normal block break-words ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Name}
                  </span>
                </div>
                <div className="sm:col-span-2">
                  <span
                    className={`text-sm sm:text-base block ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Ownership_Percent}
                  </span>
                </div>
                <div className="sm:col-span-3">
                  <span
                    className={`text-sm sm:text-base font-light block ${
                      owner.Name === "Others"
                        ? "text-gray-500"
                        : "text-gray-900"
                    }`}
                  >
                    {owner.Type}
                  </span>
                </div>
                <div className="sm:col-span-3">
                  {/* Individual shareholders don't show "Sold for" unless they have their own sale data */}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="p-3 sm:p-4 md:p-6 relative flex-1 overflow-y-auto">
          <div className="hidden sm:block absolute left-32 md:left-50 top-0 bottom-0 w-0.5 border-l border-dashed border-gray-300"></div>

          <div className="space-y-6 sm:space-y-8 md:space-y-12">
            {data.Transactions?.map((sale, index) => {
              return (
                <div
                  key={index}
                  className="relative flex flex-col sm:flex-row sm:items-start"
                >
                  <div className="w-full sm:w-32 md:w-44 sm:text-right sm:pr-4 flex-shrink-0 mb-4 sm:mb-0">
                    <p className="text-xs sm:text-sm md:text-base text-gray-500 mb-2 font-sans">
                      {formatDate(sale.Date)}
                    </p>

                    <div className="text-sm sm:text-base text-gray-700 font-normal break-words">
                      {sale.Event}
                    </div>
                  </div>

                  <div className="hidden sm:block w-3 h-3 rounded-full bg-gray-200 shadow absolute left-[7.5rem] md:left-[10.625rem] top-[2rem] z-10"></div>

                  <div className="flex-1 sm:ml-4 md:ml-6 sm:mt-[1.75rem]">
                    <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light break-words">
                      {sale.Details}
                    </p>

                    <div className="space-y-1 mb-2">
                      <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light break-words">
                        <span className="font-medium">Sell side advisors:</span>{" "}
                        {sale.Sell_Side_Advisors?.join(", ")}
                      </p>
                      <p className="text-[#6B7280] text-sm mb-3 leading-relaxed font-light break-words">
                        <span className="font-medium">Buy side advisors:</span>{" "}
                        {sale.Buy_Side_Advisors?.join(", ")}
                      </p>
                    </div>

                    {sale.Sources && sale.Sources.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {sale.Sources.map((source, sourceIndex) => (
                          <div
                            key={sourceIndex}
                            className="relative inline-flex items-center group"
                          >
                            <button
                              onClick={(e) => handleSourceClick(e, source.URL)}
                              className="inline-block text-xs text-gray-700 border border-gray-300 font-medium px-1.5 py-0.5 rounded-xl shadow-sm hover:bg-gray-50 transition-colors break-all max-w-full"
                            >
                              {getHostname(source.URL)}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {iframeUrl && (
        <IframeViewer url={iframeUrl} onClose={() => setIframeUrl(null)} />
      )}
    </>
  );
};
